import {prefixWithPipe} from "../prefixWithPipe";
import type {PropertyMap} from "../../../common/utils/objects";
import {VisitRecord} from "./visitRecord";
import {resolve} from "../../../container";
import {Dictionary} from "../../../page/elements/dictionary";
import {html, type TemplateResult} from "lit";
import {property, query, queryAll, state} from "lit/decorators.js";
import {LocatedDates} from "../../../common/utils/dates/locatedDates";
import {unsafeHTML} from "lit/directives/unsafe-html.js";
import type {FeedEntry} from "../feedEntry";
import {DisclaimerDialogFactory} from "../../../page/elements/dialog/disclaimerDialogContent";
import {LightboxDialogFactory} from "../../../page/elements/dialog/lightboxDialog";
import {relativize} from "../../../common/utils/url";
import {DialogAnchorElementContent} from "../../../page/elements/dialog/dialogAnchorElementContent";
import type {Nullable} from "../../../bootstrap/common/types";
import {trackingTeaserInteract} from "../../../tracking/trackingAttributes";
import {KEYS} from "../../../common/utils/events";

const HOVER_CLASS = "hover";

const TYPE_CLASS: PropertyMap = {
    download: "download-tile",
    default: ""
};

export abstract class SearchTile extends DialogAnchorElementContent {

    @property()
    protected entry: FeedEntry;
    @property()
    protected groupName: string;
    @state()
    protected boxed = false;

    @queryAll("a")
    private links: NodeListOf<HTMLElement>;
    @query(".tile-content")
    private tileContent: HTMLElement;

    private dictionary: Dictionary;

    protected constructor(
        private locatedDates: LocatedDates = resolve(LocatedDates),
        private visitRecord: VisitRecord = resolve(VisitRecord)
    ) {
        super();
    }

    public connectedCallback(): void {
        super.connectedCallback();
        this.dictionary = Dictionary.of(this);
    }

    protected renderTile(): TemplateResult {
        const trackingLabel = `content-search-tile-${this.headlineText()}`;
        if (this.entry.data.disclaimer || this.entry.data.lightboxImageUrl) {
            return html`
                <div
                        @mouseover=${this.addHoverClass}
                        @mouseout=${this.removeHoverClass}
                        @click=${this.handleTileClick}
                        @keydown=${this.handleTileKeydown}
                        class="tile-content ${this.boxed ? "reverse-background" : ""} ${this.typeClass()}"
                        ?download=${this.isDownload()}
                        ${trackingTeaserInteract(trackingLabel, this.entry.data.url)}
                        tabindex="0"
                >
                    ${this.renderTileContent()}
                </div>`;
        } else {
            return html`
                <eop-background-link
                        href=${this.entry.data.url}
                        @mouseover=${this.addHoverClass}
                        @mouseout=${this.removeHoverClass}
                        @click=${this.handleBackgroundLinkClick}
                        @keydown=${this.handleBackgroundLinkKeydown}
                        class="tile-content ${this.boxed ? "reverse-background" : ""} ${this.typeClass()}"
                        ?download=${this.isDownload()}
                        ${trackingTeaserInteract(trackingLabel, this.entry.data.url)}
                >
                    ${this.renderTileContent()}
                </eop-background-link>`;
        }
    }

    private renderTileContent(): TemplateResult {
        return html`
            <div class="tile-image">
                ${this.renderImageElement()}
            </div>
            <div class="tile-text">
                <div class="tile-top">
                    ${this.renderFileExtension()}
                    <div class="metadata">
                        ${this.renderNewLabel()}
                        ${this.getDate()}${this.getNewsType()}
                    </div>
                    ${this.renderHeadline()}
                    ${this.renderSubHeadline()}
                    ${this.renderFileSize()}
                </div>
                <div class="tile-bottom">
                    ${this.renderTopicLinks()}
                </div>
            </div>
        `;
    }

    protected focusableTile(): HTMLElement {
        return this.tileContent;
    }

    protected lastFocusableLink(): Nullable<HTMLElement> {
        return Array.from(this.links).last();
    }

    private handleBackgroundLinkClick(): void {
        this.recordVisit();
    }

    private handleBackgroundLinkKeydown(event: KeyboardEvent): void {
        if (event.key === KEYS.ENTER) {
            this.recordVisit();
        }
    }

    private isDownload(): boolean {
        return this.typeClass() === TYPE_CLASS.download;
    }

    private isNotVisited(): boolean {
        return this.entry.data.isNew && this.entry.recentlyVisited === false;
    }

    protected abstract renderImageElement(): Nullable<TemplateResult>;

    protected abstract renderTopicLinks(): Nullable<TemplateResult>;

    protected abstract showDescription(): boolean;

    private isDownloadTile(): boolean {
        return this.entry.data.contentType === "download";
    }

    protected removeHoverClass(e: Event): void {
        e.stopPropagation();
        this.classList.remove(HOVER_CLASS);
    }

    protected addHoverClass(e: Event): void {
        e.stopPropagation();
        this.classList.add(HOVER_CLASS);
    }

    private renderFileExtension(): Nullable<TemplateResult> {
        if (!this.isDownloadTile()) {
            return null;
        }

        return html`<span class="file-extension reverse-background">${this.entry.data.fileMetadata?.extension}</span>`;
    }

    private renderNewLabel(): Nullable<TemplateResult> {
        if (!this.isNotVisited()) {
            return null;
        }

        return html`<span class="label-new"><eop-msg key="CONTENT_SEARCH_NEW"></eop-msg></span>`;
    }

    private renderFileSize(): Nullable<TemplateResult> {
        if (!this.isDownloadTile()) {
            return null;
        }

        return html`<span class="file-size">(${this.entry.data.fileMetadata?.size})</span>`;
    }

    private renderHeadline(): Nullable<TemplateResult> {
        if (this.entry.data.lightboxImageUrl && !this.entry.data.disclaimer) {
            return html`
                <a @click=${this.handleDownloadClick} @keydown=${this.handleDownloadKeydown} download class="message-headline" target="_blank"
                   href=${relativize(this.entry.data.url)}>
                    <div class="headline-text">${unsafeHTML(this.headlineText())}</div>
                </a>`;
        }
        return html`
            <div class="message-headline">
                <div class="headline-text">${unsafeHTML(this.headlineText())}</div>
            </div>`;
    }

    protected abstract headlineText(): string;

    private renderSubHeadline(): Nullable<TemplateResult> {
        if (!this.showDescription()) {
            return null;
        }
        return html`
            <div class="message-description">${unsafeHTML(this.subHeadlineText())}</div>`;
    }

    protected abstract subHeadlineText(): string;

    private handleDownloadClick(event: Event): void {
        event.stopPropagation();
    }

    private handleDownloadKeydown(event: KeyboardEvent): void {
        if (event.key === KEYS.ENTER) {
            event.stopPropagation(); // do not open lightbox/disclaimer dialog
        }
    }

    private handleTileKeydown(event: KeyboardEvent): void {
        if (event.key === KEYS.ENTER) {
            this.handleTileClick(event);
        }
    }

    private handleTileClick(event: Event): void {
        if (this.entry.data.disclaimer) {
            event.preventDefault();
            this.createDialog(DisclaimerDialogFactory.create(
                this.entry.data.disclaimer.headline,
                this.entry.data.disclaimer.content,
                this.entry.data.url,
                this.dictionary.translate("MSG_DISCLAIMER_ACCEPT"),
                this.dictionary.translate("MSG_DISCLAIMER_DECLINE")
            ));
            this.openDialog(event);
        } else if (this.entry.data.lightboxImageUrl) {
            event.preventDefault();
            this.createDialog(LightboxDialogFactory.create(
                this.entry.data.headline ?? "",
                this.entry.data.subHeadline ?? "",
                this.entry.data.url,
                this.entry.data.lightboxImageUrl,
                this.entry.data.mediaType,
                this.entry.data.imageAltText,
                this.dictionary.translate("MSG_DOWNLOAD_IMAGE")
            ));
            this.openDialog(event);
        }
        this.recordVisit();
    }

    private recordVisit(): void {
        if (this.isNotVisited()) {
            this.visitRecord.addVisitedURL(this.entry.data.url);
        }
    }

    private typeClass(): string {
        return TYPE_CLASS[this.entry.data.contentType] ?? TYPE_CLASS.default;
    }

    private getDate(): string {
        return this.locatedDates.toLocalDateString(new Date(this.entry.data.publishedDate));
    }

    private getNewsType(): string {
        return prefixWithPipe(this.entry.data.newsType);
    }
}